import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import guardiansService from "../services/guardiansService";
import S3ImageUpload from "../components/S3ImageUpload";
import "../assets/guardianEditLaravel.css";
import { AutofillDecoys, NoAutofillInput } from "../components/NoAutofill";

const GuardianEditLaravel = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [guardian, setGuardian] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    nationalities: [],
    religions: [],
    hearAbouts: [],
    genders: [
      { id: 0, name: "Female" },
      { id: 1, name: "Male" },
      { id: 2, name: "Non-binary" },
      { id: 3, name: "Transgender" },
      { id: 4, name: "Intersex" },
      { id: 5, name: "I prefer not to say" },
      { id: 6, name: "Let me Type*" },
    ],
  });

  const [formData, setFormData] = useState({
    role_id: 9, // Guardian role
    salutation: "",
    first_name: "",
    last_name: "",
    email: "",
    email_alternative: "",
    password: "",
    password_confirmation: "",
    image: null,
    imageKey: null, // S3 key for uploaded image
    imageName: null, // File name for backend validation
    imageType: null, // File type for backend validation
    imageSize: null, // File size for backend validation
    job_title: "",
    phone_number: "",
    mobile_number: "",
    address_1: "",
    address_2: "",
    address_3: "",
    town: "",
    postcode: "",
    staff: 0,
    staff_guardian_page: 0,
    staff_property_owners: 0,
    staff_about_us_page: 0,
    biography: "",
    color: "",
    rec_notif_of_property_status: 0,
    rec_notif_of_property_id: 0,
    nationality_id: "",
    religion_id: "",
    hear_about_id: "",
    gender: "",
    gender_freetext: "",
    date_of_birth: "",
    national_insurance_number: "",
    criminal_record: 0,
    criminal_record_description: "",
    medical_condition: 0,
    medical_condition_description: "",
    next_of_kin: "",
    notes: "",
  });

  const [apiFailed, setApiFailed] = useState(false);

  useEffect(() => {
    loadGuardian();
    loadDropdownData();
  }, [id]);

  const loadGuardian = async () => {
    try {
      setLoading(true);
      setApiFailed(false);
      const data = await guardiansService.getGuardianById(id);
      setGuardian(data);

      // Populate form data
      setFormData({
        role_id: 9, // Guardian role
        salutation: data.user?.salutation || "",
        first_name: data.user?.first_name || "",
        last_name: data.user?.last_name || "",
        email: data.user?.email || "",
        email_alternative: data.user?.email_alternative || "",
        password: "",
        password_confirmation: "",
        image: data.user?.image || null,
        imageKey: data.user?.image || null,
        imageName: data.user?.image_name || null,
        imageType: data.user?.image_type || null,
        imageSize: data.user?.image_size || null,
        job_title: data.user?.job_title || "",
        phone_number: data.user?.phone_number || "",
        mobile_number: data.user?.mobile_number || "",
        address_1: data.user?.address_1 || "",
        address_2: data.user?.address_2 || "",
        address_3: data.user?.address_3 || "",
        town: data.user?.town || "",
        postcode: data.user?.postcode || "",
        staff: data.user?.staff || 0,
        staff_guardian_page: data.user?.staff_guardian_page || 0,
        staff_property_owners: data.user?.staff_property_owners || 0,
        staff_about_us_page: data.user?.staff_about_us_page || 0,
        biography: data.user?.biography || "",
        color: data.user?.color || "",
        rec_notif_of_property_status:
          data.user?.rec_notif_of_property_status || 0,
        rec_notif_of_property_id: data.user?.rec_notif_of_property_id || 0,
        nationality_id: data.nationality_id || "",
        religion_id: data.religion_id || "",
        hear_about_id: data.hear_about_id || "",
        gender: data.gender !== undefined && data.gender !== null ? String(data.gender) : "",
        gender_freetext: data.gender_freetext || "",
        date_of_birth: data.date_of_birth
          ? data.date_of_birth.split("T")[0]
          : "",
        national_insurance_number: data.national_insurance_number || "",
        criminal_record: data.criminal_record || 0,
        criminal_record_description: data.criminal_record_description || "",
        medical_condition: data.medical_condition || 0,
        medical_condition_description: data.medical_condition_description || "",
        next_of_kin: data.next_of_kin || "",
        notes: data.notes || "",
      });
    } catch (err) {
      console.error("Error loading guardian:", err);
      setApiFailed(true);
      toast.error("Failed to load guardian details");
    } finally {
      setLoading(false);
    }
  };

  const loadDropdownData = async () => {
    try {
      setApiFailed(false);
      const data = await guardiansService.getDropdownData();
      setDropdownData((prev) => ({
        ...prev,
        nationalities: data.nationalities || [],
        religions: data.religions || [],
        hearAbouts: data.hearAbouts || [],
      }));
    } catch (error) {
      console.error("Error loading dropdown data:", error);
      setApiFailed(true);
      // Return empty arrays when API fails
      setDropdownData((prev) => ({
        ...prev,
        nationalities: [],
        religions: [],
        hearAbouts: [],
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]:
          type === "checkbox" || type === "radio"
            ? type === "radio"
              ? parseInt(value)
              : checked
            : type === "file"
            ? files[0]
            : value,
      };

      // Clear description fields when radio buttons are set to "No"
      if (name === "criminal_record" && parseInt(value) === 0) {
        newData.criminal_record_description = "";
      }
      if (name === "medical_condition" && parseInt(value) === 0) {
        newData.medical_condition_description = "";
      }

      return newData;
    });
  };

  // Handle image upload from S3ImageUpload component
  const handleImageUpload = (imageKey, file) => {
    setFormData((prev) => ({
      ...prev,
      image: file, // Keep file for preview
      imageKey: imageKey, // Store S3 key for backend
      imageName: file?.name || "", // Store file name for backend validation
      imageType: file?.type || "", // Store file type for backend validation
      imageSize: file?.size || 0, // Store file size for backend validation
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Validate alternative email format if provided
      if (formData.email_alternative && formData.email_alternative.trim() !== "") {
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (!emailRegex.test(formData.email_alternative)) {
          toast.error("Please enter a valid alternative email address");
          setSaving(false);
          return;
        }
        // Check that alternative email is not the same as primary email
        if (formData.email_alternative.toLowerCase() === formData.email.toLowerCase()) {
          toast.error("Alternative email must be different from primary email");
          setSaving(false);
          return;
        }
      }

      // Prepare data for submission (JSON instead of FormData)
      const submitData = {
        ...formData,
        // Use imageKey for the image field and include additional metadata
        image: formData.imageKey || null,
        imageKey: formData.imageKey,
        imageName: formData.imageName || "",
        imageType: formData.imageType || "",
        imageSize: formData.imageSize || 0,
      };

      // Remove the File object from submission (keep imageKey)
      if (submitData.image instanceof File) {
        delete submitData.image;
      }

      // Filter out empty strings and null values - only send fields that have actual values
      const cleanedData = {};
      Object.keys(submitData).forEach((key) => {
        const value = submitData[key];
        // Only include non-empty values (skip empty strings, null, undefined)
        if (value !== "" && value !== null && value !== undefined) {
          cleanedData[key] = value;
        }
      });

      await guardiansService.updateGuardian(id, cleanedData);
      toast.success("Guardian updated successfully!");
      navigate(`/guardians/${id}`);
    } catch (error) {
      console.error("Error updating guardian:", error);
      
      // Handle validation errors with field-specific messages
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        // Display all validation errors
        Object.keys(errors).forEach((field) => {
          const fieldErrors = errors[field];
          if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            // Show first error for each field
            toast.error(`${field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: ${fieldErrors[0]}`);
          }
        });
      } else {
        // Handle general error message
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to update guardian";
        toast.error(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/guardians/${id}`);
  };

  if (loading) return <div className="loading-container">Loading...</div>;

  return (
    <div
      className="guardian-edit-laravel"
      style={{ paddingLeft: "160px", paddingRight: "160px" }}
    >
      {/* Navigation Breadcrumb */}
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          / Guardian
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">edit guardian</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <div
              className="panel panel-default panel-form"
              style={{ margin: "0" }}
            >
              <div
                className="panel-heading panel-title"
                style={{ margin: "0" }}
              >
                <h3>Edit guardian</h3>
              </div>
              <div className="panel-body">
                <form onSubmit={handleSubmit} encType="multipart/form-data" autoComplete="off">
                  <AutofillDecoys />
                  <fieldset className="form-horizontal">
                    {/* Role Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">Role</label>
                      <div className="col-sm-5">
                        <div className="form-info">Guardian</div>
                        <input
                          type="hidden"
                          name="role_id"
                          value={formData.role_id}
                          className="js-changeable-field"
                        />
                      </div>
                    </div>

                    {/* Title Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">Title</label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="salutation"
                          value={formData.salutation}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    {/* First Name Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3 label-required">
                        First Name
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          className="form-control"
                          required
                        />
                      </div>
                    </div>

                    {/* Last Name Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3 label-required">
                        Last Name*
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          className="form-control"
                          required
                        />
                      </div>
                    </div>

                    {/* Email Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3 label-required">
                        Email*
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="form-control"
                          autoComplete="off"
                          required
                        />
                      </div>
                    </div>

                    {/* Alternative Email Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Alternative Email
                      </label>
                      <div className="col-sm-5">
                        <NoAutofillInput
                          type="email"
                          name="email_alternative"
                          value={formData.email_alternative}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="e.g., alternate@example.com"
                        />
                        {formData.email_alternative && (
                          <div className="help-block" style={{ fontSize: '12px', marginTop: '4px' }}>
                            {formData.email_alternative.toLowerCase() === formData.email.toLowerCase() ? (
                              <span style={{ color: '#d9534f' }}>Alternative email must be different from primary email</span>
                            ) : /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(formData.email_alternative) ? (
                              <span style={{ color: '#5cb85c' }}>✓ Valid email format</span>
                            ) : (
                              <span style={{ color: '#d9534f' }}>Please use a valid email format</span>
                            )}
                          </div>
                        )}
                        <div className="help-block" style={{ fontSize: '11px', color: '#888' }}>
                          Guardian can also login using this email address
                        </div>
                      </div>
                    </div>

                    {/* Password Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">Password</label>
                      <div className="col-sm-5">
                        <NoAutofillInput
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                        <div className="help-block">
                          Leave empty if you don't want to change password
                        </div>
                      </div>
                    </div>

                    {/* Password Confirmation Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Password Confirmation
                      </label>
                      <div className="col-sm-5">
                        <NoAutofillInput
                          type="password"
                          name="password_confirmation"
                          value={formData.password_confirmation}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                        <div className="help-block">
                          Leave empty if you don't want to change password
                        </div>
                      </div>
                    </div>

                    {/* Photograph Field - using S3ImageUpload component */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Photograph
                      </label>
                      <div className="col-sm-5">
                        <S3ImageUpload
                          onImageUpload={handleImageUpload}
                          currentImage={formData.image}
                          folder="uploads/images/guardians"
                          helpText="The photograph must be a clear headshot of your likeness of yourself. The file must be no larger than 100MB."
                          maxSize={100 * 1024 * 1024} // 100MB
                          accept="image/*"
                          showPreview={true}
                          previewClassName="img-responsive mb-20"
                          exampleImage="/images/photograph-woman.jpg"
                        />
                      </div>
                    </div>
                    <div className="clearfix"></div>

                    {/* Job Title Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Job Title
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="job_title"
                          value={formData.job_title}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    {/* Phone Number Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Phone Number
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="phone_number"
                          value={formData.phone_number}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    {/* Mobile Number Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3 label-required">
                        Mobile Number*
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="mobile_number"
                          value={formData.mobile_number}
                          onChange={handleInputChange}
                          className="form-control"
                          required
                        />
                      </div>
                    </div>

                    {/* Address Fields */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Address 1
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="address_1"
                          value={formData.address_1}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Address 2
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="address_2"
                          value={formData.address_2}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Address 3
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="address_3"
                          value={formData.address_3}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">Town</label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="town"
                          value={formData.town}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">Postcode</label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="postcode"
                          value={formData.postcode}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    {/* Staff Field */}
                    <div className="form-group create-property-input-field-Status">
                      <label>Staff</label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "140px",
                          marginLeft: "19px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                          }}
                        >
                          <input
                            type="radio"
                            name="staff"
                            value={0}
                            checked={formData.staff === 0}
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>No</label>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                            marginLeft: "9px",
                          }}
                        >
                          <input
                            type="radio"
                            name="staff"
                            value={1}
                            checked={formData.staff === 1}
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>Yes</label>
                        </div>
                      </div>
                    </div>

                    {/* Staff-only fields - only visible when staff is Yes */}
                    {formData.staff === 1 && (
                      <>
                        {/* Display on Page Guardian */}
                        <div className="form-group create-property-input-field">
                          <label className="control-label col-sm-3">
                            Display on Page Guardian
                          </label>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "140px",
                              marginLeft: "10px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                              }}
                            >
                              <input
                                type="radio"
                                name="staff_guardian_page"
                                value={0}
                                checked={formData.staff_guardian_page === 0}
                                onChange={handleInputChange}
                                className="form-checkbox"
                              />
                              <label>No</label>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                marginLeft: "10px",
                              }}
                            >
                              <input
                                type="radio"
                                name="staff_guardian_page"
                                value={1}
                                checked={formData.staff_guardian_page === 1}
                                onChange={handleInputChange}
                                className="form-checkbox"
                              />
                              <label>Yes</label>
                            </div>
                          </div>
                        </div>

                        {/* Display on Property Owners */}
                        <div className="form-group create-property-input-field">
                          <label className="control-label col-sm-3">
                            Display on Property Owners
                          </label>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "140px",
                              marginLeft: "10px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                              }}
                            >
                              <input
                                type="radio"
                                name="staff_property_owners"
                                value={0}
                                checked={formData.staff_property_owners === 0}
                                onChange={handleInputChange}
                                className="form-checkbox"
                              />
                              <label>No</label>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                marginLeft: "10px",
                              }}
                            >
                              <input
                                type="radio"
                                name="staff_property_owners"
                                value={1}
                                checked={formData.staff_property_owners === 1}
                                onChange={handleInputChange}
                                className="form-checkbox"
                              />
                              <label>Yes</label>
                            </div>
                          </div>
                        </div>

                        {/* Display on About Us */}
                        <div className="form-group create-property-input-field">
                          <label className="control-label col-sm-3">
                            Display on About Us
                          </label>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "140px",
                              marginLeft: "10px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                              }}
                            >
                              <input
                                type="radio"
                                name="staff_about_us_page"
                                value={0}
                                checked={formData.staff_about_us_page === 0}
                                onChange={handleInputChange}
                                className="form-checkbox"
                              />
                              <label>No</label>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                marginLeft: "10px",
                              }}
                            >
                              <input
                                type="radio"
                                name="staff_about_us_page"
                                value={1}
                                checked={formData.staff_about_us_page === 1}
                                onChange={handleInputChange}
                                className="form-checkbox"
                              />
                              <label>Yes</label>
                            </div>
                          </div>
                        </div>

                        {/* Biography Field */}
                        <div className="form-group create-property-textarea-field">
                          <label className="control-label ">Biography</label>
                          <div>
                            <textarea
                              name="biography"
                              value={formData.biography}
                              onChange={handleInputChange}
                              className="form-control"
                              rows={8}
                              placeholder="Enter biography here..."
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {/* Calendar Color Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Calendar Color
                      </label>
                      <div className="col-md-5">
                        <div className="input-group colorpicker-component js-colorpicker">
                          <input
                            type="text"
                            name="color"
                            value={formData.color}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                          <span className="input-group-addon">
                            <i></i>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Notification Fields */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label">
                        Receive notification of property status
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "140px",
                          marginLeft: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                          }}
                        >
                          <input
                            type="radio"
                            name="rec_notif_of_property_status"
                            value={0}
                            checked={
                              formData.rec_notif_of_property_status === 0
                            }
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>No</label>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                            marginLeft: "10px",
                          }}
                        >
                          <input
                            type="radio"
                            name="rec_notif_of_property_status"
                            value={1}
                            checked={
                              formData.rec_notif_of_property_status === 1
                            }
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>Yes</label>
                        </div>
                      </div>
                    </div>

                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-4">
                        Receive notification of property ID
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "140px",
                          marginLeft: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                          }}
                        >
                          <input
                            type="radio"
                            name="rec_notif_of_property_id"
                            value={0}
                            checked={formData.rec_notif_of_property_id === 0}
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>No</label>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                            marginLeft: "10px",
                          }}
                        >
                          <input
                            type="radio"
                            name="rec_notif_of_property_id"
                            value={1}
                            checked={formData.rec_notif_of_property_id === 1}
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>Yes</label>
                        </div>
                      </div>
                    </div>

                    {/* Nationality Field */}
                    <div className="form-group create-property-input-field-Status">
                      <label className="control-label col-sm-3">
                        Nationality
                      </label>
                      <div className="col-sm-5">
                        <select
                          name="nationality_id"
                          value={formData.nationality_id}
                          onChange={handleInputChange}
                          className="form-control"
                        >
                          <option value="">Please Select...</option>
                          {Array.isArray(dropdownData.nationalities) && dropdownData.nationalities.map((nationality) => (
                            <option key={nationality.id} value={nationality.id}>
                              {nationality.nationality}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Religion Field */}
                    <div className="form-group create-property-input-field-Status">
                      <label className="control-label col-sm-3">Religion</label>
                      <div className="col-sm-5">
                        <select
                          name="religion_id"
                          value={formData.religion_id}
                          onChange={handleInputChange}
                          className="form-control"
                        >
                          <option value="">Please Select...</option>
                          {Array.isArray(dropdownData.religions) && dropdownData.religions.map((religion) => (
                            <option key={religion.id} value={religion.id}>
                              {religion.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Hear About Field */}
                    <div className="form-group create-property-input-field-Status">
                      <label className="control-label col-sm-3">
                        How to did you hear about Global Guardians?
                      </label>
                      <div className="col-sm-5">
                        <select
                          name="hear_about_id"
                          value={formData.hear_about_id}
                          onChange={handleInputChange}
                          className="form-control"
                        >
                          <option value="">Please Select...</option>
                          {Array.isArray(dropdownData.hearAbouts) && dropdownData.hearAbouts.map((hearAbout) => (
                            <option key={hearAbout.id} value={hearAbout.id}>
                              {hearAbout.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Gender Field */}
                    <div className="form-group create-property-input-field-Status">
                      <label className="control-label col-sm-3 label-required">
                        Gender
                      </label>
                      <div className="col-sm-5 gender-select">
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleInputChange}
                          className="form-control gender-selected"
                          required
                        >
                          <option value="">Please Select...</option>
                          {Array.isArray(dropdownData.genders) && dropdownData.genders.map((gender) => (
                            <option key={gender.id} value={gender.id}>
                              {gender.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-sm-3">
                        <input
                          type="text"
                          name="gender_freetext"
                          value={formData.gender_freetext}
                          onChange={handleInputChange}
                          className="form-control"
                          placeholder="Custom gender"
                        />
                      </div>
                    </div>

                    {/* Date of Birth Field */}
                    <div className="form-group create-property-input-field-Status">
                      <label className="control-label col-sm-3 label-required">
                        Date Of Birth
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="date"
                          name="date_of_birth"
                          value={formData.date_of_birth}
                          onChange={handleInputChange}
                          className="form-control js-datepicker"
                          required
                        />
                      </div>
                    </div>

                    {/* National Insurance Number Field */}
                    <div className="form-group create-property-input-field-Status">
                      <label className="control-label col-sm-3">
                        National Insurance Number
                      </label>
                      <div className="col-sm-5">
                        <input
                          type="text"
                          name="national_insurance_number"
                          value={formData.national_insurance_number}
                          onChange={handleInputChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    {/* Criminal Record Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Any Criminal Record?
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "140px",
                          marginLeft: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                          }}
                        >
                          <input
                            type="radio"
                            name="criminal_record"
                            value={0}
                            checked={formData.criminal_record === 0}
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>No</label>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                            marginLeft: "10px",
                          }}
                        >
                          <input
                            type="radio"
                            name="criminal_record"
                            value={1}
                            checked={formData.criminal_record === 1}
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>Yes</label>
                        </div>
                      </div>
                    </div>

                    {/* Criminal Record Description - only visible when Yes is selected */}
                    {formData.criminal_record === 1 && (
                      <div className="form-group create-property-textarea-field">
                        <label className="control-label">
                          Criminal Record Description
                        </label>
                        <div>
                          <textarea
                            name="criminal_record_description"
                            value={formData.criminal_record_description}
                            onChange={handleInputChange}
                            className="form-control"
                            rows={4}
                            placeholder="Please provide details of any criminal record..."
                          />
                        </div>
                      </div>
                    )}

                    {/* Medical Condition Field */}
                    <div className="form-group create-property-input-field">
                      <label className="control-label col-sm-3">
                        Any Medical Condition?
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "140px",
                          marginLeft: "10px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                          }}
                        >
                          <input
                            type="radio"
                            name="medical_condition"
                            value={0}
                            checked={formData.medical_condition === 0}
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>No</label>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "40px",
                            marginLeft: "10px",
                          }}
                        >
                          <input
                            type="radio"
                            name="medical_condition"
                            value={1}
                            checked={formData.medical_condition === 1}
                            onChange={handleInputChange}
                            className="form-checkbox"
                          />
                          <label>Yes</label>
                        </div>
                      </div>
                    </div>

                    {/* Medical Condition Description - only visible when Yes is selected */}
                    {formData.medical_condition === 1 && (
                      <div className="form-group create-property-textarea-field">
                        <label className="control-label">
                          Medical Condition Description
                        </label>
                        <div className="">
                          <textarea
                            name="medical_condition_description"
                            value={formData.medical_condition_description}
                            onChange={handleInputChange}
                            className="form-control"
                            rows={4}
                            placeholder="Please provide details of any medical condition..."
                          />
                        </div>
                      </div>
                    )}

                    {/* Next of Kin Field */}
                    <div className="form-group create-property-textarea-field">
                      <label className="control-label">
                        Details of Next Kin
                      </label>
                      <div className="">
                        <textarea
                          name="next_of_kin"
                          value={formData.next_of_kin}
                          onChange={handleInputChange}
                          className="form-control"
                          rows={4}
                        />
                      </div>
                    </div>

                    {/* Notes Field */}
                    <div className="form-group create-property-textarea-field">
                      <label className="control-label col-sm-3">Notes</label>
                      <div>
                        <textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          className="form-control"
                          rows={16}
                        />
                      </div>
                    </div>
                  </fieldset>

                  {/* Submit Button */}
                  <fieldset className="form-horizontal">
                    <div className="form-group text-right">
                      <div
                        style={{
                          marginLeft: "auto",
                          paddingRight: "230px",
                        }}
                      >
                        <button
                          type="submit"
                          className="btn-add-user"
                          disabled={loading}
                        >
                          <i className="glyphicon glyphicon-plus"></i> Edit
                          Guardian
                        </button>
                      </div>
                    </div>
                  </fieldset>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuardianEditLaravel;
